import React, { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import { useI18n } from "../i18n";
import {
  Cloud, Loader2, RefreshCw, CircleDot, Activity, Zap, TrendingUp, TrendingDown,
  Server, Users, KeyRound, Search, ShieldCheck, AlertCircle,
} from "lucide-react";

/**
 * Page publique client — "Mes trades copiés" (Cloud Copytrading).
 * Le client saisit sa Pairing Key et voit en temps réel :
 *  - Ses Masters/Slaves connectés (status heartbeat)
 *  - Les events récents des dernières 24h
 * Auto-refresh 8s tant que la Pairing Key est valide.
 */
export default function ClientCopytrading() {
  const { t: _t } = useI18n();
  const [pairingKey, setPairingKey] = useState(() => {
    try { return localStorage.getItem("dtsp_copy_pairing_key") || ""; } catch { return ""; }
  });
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchStatus = useCallback(async (key) => {
    if (!key || key.length < 4) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.get(`/copy/status?pairing_key=${encodeURIComponent(key)}&limit=50`);
      setStatus(data);
      setLastRefresh(new Date());
      try { localStorage.setItem("dtsp_copy_pairing_key", key); } catch (_e) { /* storage unavailable */ }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur de récupération");
      setStatus(null);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (!pairingKey) return undefined;
    fetchStatus(pairingKey);
    const iv = setInterval(() => fetchStatus(pairingKey), 8000);
    return () => clearInterval(iv);
  }, [pairingKey, fetchStatus]);

  const onSubmit = (e) => {
    e.preventDefault();
    const key = (e.target.pairing_key.value || "").trim();
    setPairingKey(key);
  };

  const onClear = () => {
    setPairingKey("");
    setStatus(null);
    try { localStorage.removeItem("dtsp_copy_pairing_key"); } catch (_e) { /* storage unavailable */ }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("fr-FR"); } catch { return iso; }
  };

  const isRecent = (iso) => {
    if (!iso) return false;
    try { return (Date.now() - new Date(iso).getTime()) < 90000; } catch { return false; }
  };

  const eventTypeIcon = (t) => {
    if (t?.startsWith("OPEN")) return <TrendingUp className="h-3.5 w-3.5" />;
    if (t?.startsWith("CLOSE")) return <TrendingDown className="h-3.5 w-3.5" />;
    if (t === "MODIFY_SLTP") return <Activity className="h-3.5 w-3.5" />;
    return <Zap className="h-3.5 w-3.5" />;
  };

  const eventTypeColor = (t) => {
    if (t?.startsWith("OPEN")) return "text-emerald-400 bg-emerald-500/10 border-emerald-500/30";
    if (t?.startsWith("CLOSE")) return "text-orange-400 bg-orange-500/10 border-orange-500/30";
    if (t === "MODIFY_SLTP") return "text-cyan-400 bg-cyan-500/10 border-cyan-500/30";
    if (t === "CANCEL_PENDING") return "text-zinc-400 bg-zinc-500/10 border-zinc-500/30";
    return "text-white bg-white/10 border-white/20";
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-16" data-testid="client-copytrading">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <div className="p-3 border border-cyan-400/40 rounded-lg bg-cyan-500/10">
          <Cloud className="h-7 w-7 text-cyan-400" />
        </div>
        <div>
          <h1 className="text-3xl sm:text-4xl font-light tracking-tight">Mes trades copiés</h1>
          <p className="text-sm text-zinc-400">Cross-VPS · monitoring temps réel de votre groupe Copytrading</p>
        </div>
      </div>

      {/* Formulaire Pairing Key */}
      {!pairingKey && (
        <form onSubmit={onSubmit} className="border border-white/[0.06] rounded-2xl p-8 max-w-xl" data-testid="pairing-form">
          <div className="flex items-center gap-2 mb-4">
            <KeyRound className="h-5 w-5 text-cyan-400" />
            <h2 className="text-lg font-medium">Entrez votre Pairing Key</h2>
          </div>
          <p className="text-sm text-zinc-500 mb-4">
            Le code unique de votre groupe Copytrading (paramètre <code className="text-cyan-400 font-mono">Copy_Pairing_Key</code> configuré dans votre EA).
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              name="pairing_key"
              placeholder="Ex: DRT-GRP-001"
              required
              minLength={4}
              className="flex-1 bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-white font-mono focus:border-cyan-400 focus:outline-none"
              data-testid="pairing-input"
            />
            <button
              type="submit"
              className="px-6 py-3 bg-cyan-500 hover:bg-cyan-400 text-black font-medium rounded-lg inline-flex items-center gap-2"
              data-testid="pairing-submit"
            >
              <Search className="h-4 w-4" />
              Consulter
            </button>
          </div>
          <div className="mt-6 text-xs text-zinc-500 flex items-start gap-2">
            <ShieldCheck className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
            <span>Votre Pairing Key est confidentielle. Elle est stockée uniquement dans votre navigateur (localStorage) et n&apos;est jamais transmise à d&apos;autres serveurs.</span>
          </div>
        </form>
      )}

      {/* Status */}
      {pairingKey && (
        <div className="space-y-6">
          {/* Bandeau info */}
          <div className="flex items-center justify-between flex-wrap gap-3 border border-white/[0.06] rounded-lg px-5 py-3">
            <div className="flex items-center gap-3">
              <span className="text-sm text-zinc-400">Pairing Key :</span>
              <span className="font-mono text-cyan-400" data-testid="active-pairing-key">{pairingKey}</span>
            </div>
            <div className="flex items-center gap-3 text-xs text-zinc-500">
              {lastRefresh && <span>Rafraîchi à {lastRefresh.toLocaleTimeString("fr-FR")}</span>}
              <span className="inline-flex items-center gap-1">
                <CircleDot className="h-3 w-3 text-emerald-400 animate-pulse" />
                Auto 8s
              </span>
              <button
                onClick={() => fetchStatus(pairingKey)}
                disabled={busy}
                className="inline-flex items-center gap-1 px-2 py-1 border border-white/10 rounded hover:border-white/30 disabled:opacity-50"
                data-testid="copy-refresh"
              >
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              </button>
              <button
                onClick={onClear}
                className="text-zinc-500 hover:text-white"
                data-testid="copy-clear"
              >
                Changer de clé
              </button>
            </div>
          </div>

          {/* Erreur */}
          {err && (
            <div className="border border-red-500/30 bg-red-500/10 rounded-lg p-4 flex items-start gap-3" data-testid="copy-error">
              <AlertCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-red-300 font-medium">Impossible de charger</p>
                <p className="text-sm text-zinc-400">{err}</p>
              </div>
            </div>
          )}

          {/* KPI Cards */}
          {status && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="border border-white/[0.06] rounded-lg p-5" data-testid="kpi-masters">
                <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-wider mb-2">
                  <Server className="h-3.5 w-3.5" /> Masters connectés
                </div>
                <div className="text-3xl font-light font-mono">
                  <span className={status.masters_count > 0 ? "text-emerald-400" : "text-zinc-600"}>
                    {status.masters_count}
                  </span>
                </div>
              </div>
              <div className="border border-white/[0.06] rounded-lg p-5" data-testid="kpi-slaves">
                <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-wider mb-2">
                  <Users className="h-3.5 w-3.5" /> Slaves connectés
                </div>
                <div className="text-3xl font-light font-mono">
                  <span className={status.slaves_count > 0 ? "text-cyan-400" : "text-zinc-600"}>
                    {status.slaves_count}
                  </span>
                </div>
              </div>
              <div className="border border-white/[0.06] rounded-lg p-5" data-testid="kpi-events">
                <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-wider mb-2">
                  <Activity className="h-3.5 w-3.5" /> Events (24h)
                </div>
                <div className="text-3xl font-light font-mono text-white">{status.events_count}</div>
              </div>
            </div>
          )}

          {/* Sessions actives */}
          {status && (status.masters.length > 0 || status.slaves.length > 0) && (
            <div className="border border-white/[0.06] rounded-lg overflow-hidden">
              <div className="px-4 py-3 bg-white/[0.02] border-b border-white/[0.06] text-sm text-zinc-300">
                Sessions actives
              </div>
              <div className="divide-y divide-white/[0.04]">
                {[...status.masters, ...status.slaves].map((s) => (
                  <div key={s.session_id} className="px-4 py-3 flex items-center justify-between text-sm">
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-mono ${s.role === "MASTER" ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30" : "bg-cyan-500/10 text-cyan-400 border border-cyan-500/30"}`}>
                        {s.role}
                      </span>
                      <span className="font-mono text-white">{s.master_id || "—"}</span>
                      {s.stats?.account && <span className="text-xs text-zinc-500 font-mono">MT5 #{s.stats.account}</span>}
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <CircleDot className={`h-3 w-3 ${isRecent(s.last_heartbeat) ? "text-emerald-400 animate-pulse" : "text-zinc-600"}`} />
                      <span className="text-zinc-500">{formatDate(s.last_heartbeat)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Timeline events */}
          {status && (
            <div className="border border-white/[0.06] rounded-lg overflow-hidden">
              <div className="px-4 py-3 bg-white/[0.02] border-b border-white/[0.06] text-sm text-zinc-300">
                Timeline events <span className="text-xs text-zinc-500 ml-2">(dernières 24h)</span>
              </div>
              {status.events.length === 0 ? (
                <div className="p-8 text-center text-sm text-zinc-500" data-testid="no-events">
                  Aucun event dans les dernières 24h.
                  {status.masters_count === 0 && status.slaves_count === 0 && (
                    <p className="mt-2 text-xs">Vérifiez que votre EA est bien lancé avec <code className="text-cyan-400 font-mono">Copy_Cloud_Enable = true</code>.</p>
                  )}
                </div>
              ) : (
                <div className="divide-y divide-white/[0.04]">
                  {status.events.map((e, i) => (
                    <div key={`${e.uuid}-${i}`} className="px-4 py-3 flex items-center gap-4 text-sm hover:bg-white/[0.02]" data-testid={`event-row-${i}`}>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border font-mono ${eventTypeColor(e.event_type)}`}>
                        {eventTypeIcon(e.event_type)}
                        {e.event_type}
                      </span>
                      <span className="font-mono text-white min-w-[80px]">{e.symbol || "—"}</span>
                      {e.direction && (
                        <span className={`text-xs font-mono ${e.direction === "BUY" ? "text-emerald-400" : "text-red-400"}`}>
                          {e.direction}
                        </span>
                      )}
                      <span className="text-xs text-zinc-400 font-mono">Vol {e.volume?.toFixed(2) || "—"}</span>
                      <span className="text-xs text-zinc-500 font-mono">@ {e.price?.toFixed(5) || "—"}</span>
                      {e.strategy && (
                        <span className="text-xs text-cyan-400 font-mono">{e.strategy}</span>
                      )}
                      <span className="ml-auto text-xs text-zinc-500 font-mono">{formatDate(e.created_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
